import React, { useState, useEffect, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { toast } from "react-toastify";
import { get, post, put, del } from "../../services/api";
import CustomDatatableGrid from "../../components/CustomDatatableGrid";
import Select from "react-select";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { useNavigate } from "react-router-dom";

function FormTemplatesTab() {
  const token = localStorage.getItem("token");
  const navigate = useNavigate();
  const [triggerRerender, setTriggerRerender] = useState(false);
  const [formTemplatesData, setFormTemplatesData] = useState([]);
  const [questionsData, setQuestionsData] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [availableQuestions, setAvailableQuestions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showQuestionsModal, setShowQuestionsModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [categoriesData, setCategoriesData] = useState([]);

  // Mock data removed

  const {
    register,
    formState: { errors },
    handleSubmit,
    setValue,
    getValues,
    reset,
    watch,
    control,
  } = useForm({
    defaultValues: {
      id: null,
      name: "",
      description: "",
      version: "1.0",
      isActive: true,
      questions: [],
    },
  });

  // Fetch form templates
  const getFormTemplates = async () => {
    /*if (!token) {
      console.error("Unauthorized.");
      return;
    }*/

    try {
      const response = await get(`QAForm/templates`);

      if (response && Array.isArray(response)) {
        setFormTemplatesData(response);
      } else if (response && response.data && Array.isArray(response.data)) {
        setFormTemplatesData(response.data);
      } else {
        setFormTemplatesData([]);
      }
    } catch (error) {
      console.error("Error fetching form templates:", error);
      toast.error("Failed to load form templates");
      setFormTemplatesData([]);
    }
  };

  // Fetch available questions
  const getAvailableQuestions = async () => {
    try {
      const response = await get(`QAForm/questions`);

      if (response && Array.isArray(response)) {
        setQuestionsData(response);
        setAvailableQuestions(response);
      } else if (response && response.data && Array.isArray(response.data)) {
        setQuestionsData(response.data);
        setAvailableQuestions(response.data);
      } else {
        setQuestionsData([]);
        setAvailableQuestions([]);
      }
    } catch (error) {
      console.error("Error fetching questions:", error);
      toast.error("Failed to load questions");
      setQuestionsData([]);
      setAvailableQuestions([]);
    }
  };

  // Fetch categories
  const getCategories = async () => {
    try {
      const response = await get(`QAForm/categories`);

      if (response && Array.isArray(response)) {
        setCategoriesData(response);
      } else if (response && response.data && Array.isArray(response.data)) {
        setCategoriesData(response.data);
      } else {
        setCategoriesData([]);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      setCategoriesData([]);
    }
  };

  // Save/Update form template
  const saveFormTemplate = async (data) => {
    setIsSubmitting(true);
    /*if (!token) {
      toast.error("Unauthorized access");
      setIsSubmitting(false);
      return;
    }*/

    try {
      const apiData = {
        ...data,
        questionIds: data.questions || [],
      };

      let response;
      if (data.id) {
        // Update existing template
        response = await post(`QAForm/form-templates/${data.id}`, apiData);
      } else {
        // Create new template
        response = await post(`QAForm/templates`, apiData);
      }

      if (response) {
        toast.success(
          data.id
            ? "Form template updated successfully!"
            : "Form template created successfully!"
        );
        setTriggerRerender((prev) => !prev);
        resetForm();
        getFormTemplates(); // Refresh list immediately
      }
    } catch (error) {
      console.error("Error saving form template:", error);
      toast.error("Failed to save form template");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Edit form template
  const handleEdit = async (row) => {
    try {
      // Fetch full details
      const response = await get(`QAForm/form-templates/${row.id}`);
      const template = response || row;

      const questions = template.questions || [];
      const questionIds = questions.map((q) => q.id);

      reset({
        id: template.id,
        name: template.name,
        description: template.description,
        version: template.version,
        isActive: template.isActive,
        questions: questionIds,
      });

      // Set selected questions for the template
      // template.questions contains full objects from API
      setSelectedQuestions(questions);

      // Update available questions (exclude already selected)
      // Ensure questionsData is populated from initial load
      const availableQs = questionsData.filter(
        (q) => !questionIds.includes(q.id)
      );
      setAvailableQuestions(availableQs);
    } catch (error) {
      console.error("Error editing form template:", error);
      toast.error("Failed to load form template data");
    }
  };

  // View form template details
  const handleView = (row) => {
    // Navigate to template details or show in modal
    toast.info(`Viewing template: ${row.name}`);
    // You can implement detailed view here
  };

  // Toggle status
  const handleToggleStatus = async (rowId) => {
    const template = formTemplatesData.find((ft) => ft.id === rowId);

    const confirmToggle = window.confirm(
      `Are you sure you want to ${
        template.isActive ? "deactivate" : "activate"
      } this form template?`
    );

    if (confirmToggle) {
      try {
        await post(`QAForm/templates/${rowId}/toggle-status`);

        toast.success(
          `Form template ${
            template.isActive ? "deactivated" : "activated"
          } successfully!`
        );
        getFormTemplates(); // Refresh list
        setTriggerRerender((prev) => !prev);
      } catch (error) {
        console.error("Error toggling status:", error);
        toast.error("Failed to update form template status");
      }
    }
  };

  // Delete form template
  const handleDelete = async (rowId) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this form template?"
    );

    if (confirmDelete) {
      try {
        await del(`QAForm/templates/${rowId}`);

        toast.success("Form template deleted successfully!");
        getFormTemplates(); // Refresh list
        setTriggerRerender((prev) => !prev);
      } catch (error) {
        console.error("Error deleting form template:", error);
        toast.error("Failed to delete form template");
      }
    }
  };

  // Open questions selection modal
  const openQuestionsModal = () => {
    setShowQuestionsModal(true);
    // Reset search and filter
    setSearchTerm("");
    setSelectedCategory("");
    // Show all questions except already selected ones
    const selectedQuestionIds = getValues("questions") || [];
    const availableQs = questionsData.filter(
      (q) => !selectedQuestionIds.includes(q.id)
    );
    setAvailableQuestions(availableQs);
  };

  // Close questions modal
  const closeQuestionsModal = () => {
    setShowQuestionsModal(false);
    setSearchTerm("");
    setSelectedCategory("");
  };

  // Add question to template
  const addQuestionToTemplate = (question) => {
    const currentQuestions = [...selectedQuestions, question];
    setSelectedQuestions(currentQuestions);

    // Update form value
    const questionIds = currentQuestions.map((q) => q.id);
    setValue("questions", questionIds);

    // Remove from available questions
    setAvailableQuestions((prev) => prev.filter((q) => q.id !== question.id));
  };

  // Remove question from template
  const removeQuestionFromTemplate = (questionId) => {
    const questionToRemove = selectedQuestions.find((q) => q.id === questionId);
    const newSelectedQuestions = selectedQuestions.filter(
      (q) => q.id !== questionId
    );
    setSelectedQuestions(newSelectedQuestions);

    // Update form value
    const questionIds = newSelectedQuestions.map((q) => q.id);
    setValue("questions", questionIds);

    // Add back to available questions
    if (questionToRemove) {
      setAvailableQuestions((prev) => [...prev, questionToRemove]);
    }
  };

  // Move question up in order
  const moveQuestionUp = (index) => {
    if (index > 0) {
      const newSelectedQuestions = [...selectedQuestions];
      [newSelectedQuestions[index], newSelectedQuestions[index - 1]] = [
        newSelectedQuestions[index - 1],
        newSelectedQuestions[index],
      ];
      setSelectedQuestions(newSelectedQuestions);

      // Update form value
      const questionIds = newSelectedQuestions.map((q) => q.id);
      setValue("questions", questionIds);
    }
  };

  // Move question down in order
  const moveQuestionDown = (index) => {
    if (index < selectedQuestions.length - 1) {
      const newSelectedQuestions = [...selectedQuestions];
      [newSelectedQuestions[index], newSelectedQuestions[index + 1]] = [
        newSelectedQuestions[index + 1],
        newSelectedQuestions[index],
      ];
      setSelectedQuestions(newSelectedQuestions);

      // Update form value
      const questionIds = newSelectedQuestions.map((q) => q.id);
      setValue("questions", questionIds);
    }
  };

  // Filter available questions
  const filteredAvailableQuestions = availableQuestions.filter((question) => {
    const matchesSearch =
      searchTerm === "" ||
      question.questionText.toLowerCase().includes(searchTerm.toLowerCase()) ||
      question.categoryName.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCategory =
      selectedCategory === "" || question.categoryName === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  // Reset form
  const resetForm = () => {
    reset({
      id: null,
      name: "",
      description: "",
      version: "1.0",
      isActive: true,
      questions: [],
    });
    setSelectedQuestions([]);
    setAvailableQuestions(questionsData);
  };

  // Input change handler
  const handleChangeOfInputWithLengthCheck = (fieldName) => (e) => {
    const value = e.target.value;
    setValue(fieldName, value, { shouldValidate: true });
  };

  // Fetch data on mount
  useEffect(() => {
    getFormTemplates();
    getAvailableQuestions();
    getCategories();
  }, [triggerRerender]);

  // Export to Excel
  const exportToExcel = () => {
    const filteredData = formTemplatesData.map((row, index) => ({
      "Sr No": index + 1,
      "Template Name": row.name,
      Description: row.description,
      Version: row.version,
      "Question Count": row.questionCount,
      Status: row.isActive ? "Active" : "Inactive",
      "Created Date": new Date(row.createdAt).toLocaleDateString(),
      "Last Updated": new Date(row.updatedAt).toLocaleDateString(),
    }));

    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Form Templates");
    XLSX.writeFile(workbook, "FormTemplates.xlsx");
  };

  // Export to PDF
  const exportToPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    const title = "Form Templates";
    const titleFontSize = 12;
    doc.setFontSize(titleFontSize);
    doc.setFont("helvetica", "bold");
    const textWidth = doc.getTextWidth(title);
    const titleX = (pageWidth - textWidth) / 2;
    const titleY = 20;
    doc.text(title, titleX, titleY);

    const headers = [
      "Sr No",
      "Template Name",
      "Description",
      "Version",
      "Question Count",
      "Status",
    ];

    const bodyData = formTemplatesData.map((row, index) => [
      index + 1,
      row.name,
      row.description.length > 40
        ? row.description.substring(0, 40) + "..."
        : row.description,
      row.version,
      row.questionCount,
      row.isActive ? "Active" : "Inactive",
    ]);

    doc.autoTable({
      head: [headers],
      body: bodyData,
      theme: "striped",
      startY: titleY + 10,
      headStyles: {
        fillColor: [132, 23, 26],
        textColor: [255, 255, 255],
      },
      styles: {
        fontSize: 8,
        cellPadding: 3,
      },
      columnStyles: {
        0: { cellWidth: 15 },
        1: { cellWidth: 40 },
        2: { cellWidth: 60 },
        3: { cellWidth: 20 },
        4: { cellWidth: 25 },
        5: { cellWidth: 25 },
      },
    });

    doc.save("FormTemplates.pdf");
  };

  // Table columns
  const columns = useMemo(
    () => [
      {
        accessorKey: "mrt-row-numbers",
        header: "Sr No.",
        enableColumnOrdering: false,
        enableEditing: false,
        enableSorting: false,
        size: 60,
        Cell: ({ row }) => row.index + 1,
      },
      {
        accessorKey: "name",
        header: "Template Name",
        size: 200,
      },
      {
        accessorKey: "description",
        header: "Description",
        size: 300,
        Cell: ({ row }) => (
          <div
            style={{
              maxWidth: "300px",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {row.original.description}
          </div>
        ),
      },
      {
        accessorKey: "version",
        header: "Version",
        size: 100,
      },
      {
        accessorKey: "questionCount",
        header: "Questions",
        size: 100,
        Cell: ({ row }) => (
          <span className="badge bg-info">
            {row.original.questionCount} questions
          </span>
        ),
      },
      {
        accessorKey: "isActive",
        header: "Status",
        size: 100,
        Cell: ({ row }) => (
          <span
            className={`badge ${
              row.original.isActive ? "bg-success" : "bg-danger"
            }`}
          >
            {row.original.isActive ? "Active" : "Inactive"}
          </span>
        ),
      },
      {
        accessorKey: "actions",
        header: "Action",
        size: 180,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <div className="d-flex gap-1">
            <button
              type="button"
              className="btn p-0"
              onClick={() => handleView(row.original)}
              data-bs-toggle="tooltip"
              title="View Details"
            >
              <i className="fas fa-eye"></i>
            </button>
            <button
              type="button"
              className="btn p-0"
              onClick={() => handleEdit(row.original)}
              data-bs-toggle="tooltip"
              title="Edit Template"
            >
              <i className="fas fa-pencil"></i>
            </button>
            <button
              type="button"
              className="btn p-0"
              onClick={() => handleToggleStatus(row.original.id)}
              data-bs-toggle="tooltip"
              title={row.original.isActive ? "Deactivate" : "Activate"}
            >
              <i
                className={`fas ${
                  row.original.isActive ? "fa-ban" : "fa-check"
                }`}
              ></i>
            </button>
            <button
              type="button"
              className="btn p-0"
              onClick={() => handleDelete(row.original.id)}
              data-bs-toggle="tooltip"
              title="Delete Template"
            >
              <i className="fas fa-trash"></i>
            </button>
          </div>
        ),
      },
    ],
    []
  );

  return (
    <div className="row g-3">
      {/* Questions Selection Modal */}
      {showQuestionsModal && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Select Questions for Template</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeQuestionsModal}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  {/* Left: Available Questions */}
                  <div className="col-md-6">
                    <div className="card h-100">
                      <div className="card-header">
                        <h6 className="card-title mb-0">Available Questions</h6>
                      </div>
                      <div className="card-body">
                        {/* Search and Filter */}
                        <div className="row g-2 mb-3">
                          <div className="col-md-8">
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="Search questions..."
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                            />
                          </div>
                          <div className="col-md-4">
                            <select
                              className="form-select form-select-sm"
                              value={selectedCategory}
                              onChange={(e) =>
                                setSelectedCategory(e.target.value)
                              }
                            >
                              <option value="">All Categories</option>
                              {categoriesData.map((category) => (
                                <option key={category.id} value={category.name}>
                                  {category.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {/* Available Questions List */}
                        <div
                          className="available-questions-list"
                          style={{ maxHeight: "400px", overflowY: "auto" }}
                        >
                          {filteredAvailableQuestions.length > 0 ? (
                            filteredAvailableQuestions.map((question) => (
                              <div key={question.id} className="card mb-2">
                                <div className="card-body p-2">
                                  <div className="d-flex justify-content-between align-items-start">
                                    <div>
                                      <h6 className="mb-1">
                                        {question.questionText}
                                      </h6>
                                      <div className="small">
                                        <span className="badge bg-secondary me-1">
                                          {question.categoryName}
                                        </span>
                                        <span className="badge bg-info me-1">
                                          {question.questionType}
                                        </span>
                                        {question.isRequired && (
                                          <span className="badge bg-warning">
                                            Required
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <button
                                      type="button"
                                      className="btn btn-primary btn-sm"
                                      onClick={() =>
                                        addQuestionToTemplate(question)
                                      }
                                    >
                                      <i className="fas fa-plus"></i> Add
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="text-center p-4 text-muted">
                              No questions available
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="card-footer">
                        <small className="text-muted">
                          Found {filteredAvailableQuestions.length} questions
                        </small>
                      </div>
                    </div>
                  </div>

                  {/* Right: Selected Questions */}
                  <div className="col-md-6">
                    <div className="card h-100">
                      <div className="card-header">
                        <h6 className="card-title mb-0">
                          Selected Questions ({selectedQuestions.length})
                        </h6>
                      </div>
                      <div className="card-body">
                        <div
                          className="selected-questions-list"
                          style={{ maxHeight: "400px", overflowY: "auto" }}
                        >
                          {selectedQuestions.length > 0 ? (
                            selectedQuestions.map((question, index) => (
                              <div key={question.id} className="card mb-2">
                                <div className="card-body p-2">
                                  <div className="d-flex justify-content-between align-items-start">
                                    <div>
                                      <div className="d-flex align-items-center mb-1">
                                        <span className="badge bg-light text-dark me-2">
                                          {index + 1}
                                        </span>
                                        <h6 className="mb-0">
                                          {question.questionText}
                                        </h6>
                                      </div>
                                      <div className="small">
                                        <span className="badge bg-secondary me-1">
                                          {question.categoryName}
                                        </span>
                                        <span className="badge bg-info me-1">
                                          {question.questionType}
                                        </span>
                                        {question.isRequired && (
                                          <span className="badge bg-warning">
                                            Required
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <div className="d-flex gap-1">
                                      <button
                                        type="button"
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={() => moveQuestionUp(index)}
                                        disabled={index === 0}
                                        title="Move Up"
                                      >
                                        <i className="fas fa-arrow-up"></i>
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={() => moveQuestionDown(index)}
                                        disabled={
                                          index === selectedQuestions.length - 1
                                        }
                                        title="Move Down"
                                      >
                                        <i className="fas fa-arrow-down"></i>
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-danger btn-sm"
                                        onClick={() =>
                                          removeQuestionFromTemplate(
                                            question.id
                                          )
                                        }
                                        title="Remove"
                                      >
                                        <i className="fas fa-times"></i>
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="text-center p-4 text-muted">
                              No questions selected yet
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="card-footer">
                        <div className="d-flex justify-content-between align-items-center">
                          <small className="text-muted">
                            Drag and drop or use arrows to reorder
                          </small>
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => {
                              setSelectedQuestions([]);
                              setValue("questions", []);
                              setAvailableQuestions(questionsData);
                            }}
                          >
                            Clear All
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={closeQuestionsModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={closeQuestionsModal}
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Left Side - Form */}
      <div className="col-md-6">
        <div className="card">
          <div className="card-header">
            <h5 className="card-title mb-0">
              {getValues("id")
                ? "Edit Form Template"
                : "Create New Form Template"}
            </h5>
          </div>
          <div className="card-body">
            <form
              className="row g-3 new-form-style"
              onSubmit={handleSubmit(saveFormTemplate)}
            >
              <input type="hidden" {...register("id")} />

              <div className="col-md-12">
                <label className="form-label form-label-new">
                  Template Name<span className="text-danger fs-9">*</span>
                </label>
                <input
                  {...register("name", {
                    required: "Template name is required.",
                    maxLength: {
                      value: 100,
                      message: "Maximum of 100 characters allowed.",
                    },
                  })}
                  type="text"
                  className="form-control"
                  placeholder="Enter template name"
                  onChange={handleChangeOfInputWithLengthCheck("name")}
                />
                {errors.name && (
                  <small className="text-danger">{errors.name.message}</small>
                )}
              </div>

              <div className="col-md-12">
                <label className="form-label form-label-new">Description</label>
                <textarea
                  {...register("description", {
                    maxLength: {
                      value: 500,
                      message: "Maximum of 500 characters allowed.",
                    },
                  })}
                  className="form-control"
                  rows="3"
                  placeholder="Enter template description"
                  onChange={handleChangeOfInputWithLengthCheck("description")}
                />
                {errors.description && (
                  <small className="text-danger">
                    {errors.description.message}
                  </small>
                )}
              </div>

              <div className="col-md-6">
                <label className="form-label form-label-new">
                  Version<span className="text-danger fs-9">*</span>
                </label>
                <input
                  {...register("version", {
                    required: "Version is required.",
                    pattern: {
                      value: /^\d+\.\d+$/,
                      message: "Version must be in format X.X (e.g., 1.0, 2.1)",
                    },
                  })}
                  type="text"
                  className="form-control"
                  placeholder="e.g., 1.0"
                />
                {errors.version && (
                  <small className="text-danger">
                    {errors.version.message}
                  </small>
                )}
              </div>

              <div className="col-md-6">
                <label className="form-label form-label-new">Status</label>
                <div className="form-check">
                  <input
                    {...register("isActive")}
                    className="form-check-input"
                    type="checkbox"
                    id="isActive"
                    defaultChecked
                  />
                  <label className="form-check-label" htmlFor="isActive">
                    Active Template
                  </label>
                </div>
              </div>

              {/* Selected Questions Preview */}
              <div className="col-md-12">
                <div className="card">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h6 className="card-title mb-0">
                      Selected Questions ({selectedQuestions.length})
                    </h6>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={openQuestionsModal}
                    >
                      <i className="fas fa-plus me-1"></i> Manage Questions
                    </button>
                  </div>
                  <div className="card-body">
                    {selectedQuestions.length > 0 ? (
                      <div className="selected-questions-preview">
                        <ol className="list-group list-group-numbered">
                          {selectedQuestions.map((question, index) => (
                            <li
                              key={question.id}
                              className="list-group-item d-flex justify-content-between align-items-start"
                            >
                              <div className="ms-2 me-auto">
                                <div className="fw-bold">
                                  {question.questionText}
                                </div>
                                <small>
                                  <span className="badge bg-secondary me-1">
                                    {question.categoryName}
                                  </span>
                                  <span className="badge bg-info me-1">
                                    {question.questionType}
                                  </span>
                                  {question.isRequired && (
                                    <span className="badge bg-warning">
                                      Required
                                    </span>
                                  )}
                                </small>
                              </div>
                              <button
                                type="button"
                                className="btn btn-danger btn-sm"
                                onClick={() =>
                                  removeQuestionFromTemplate(question.id)
                                }
                              >
                                <i className="fas fa-times"></i>
                              </button>
                            </li>
                          ))}
                        </ol>
                      </div>
                    ) : (
                      <div className="text-center p-3 text-muted">
                        <i className="fas fa-question-circle fa-2x mb-2"></i>
                        <p>No questions selected yet</p>
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm"
                          onClick={openQuestionsModal}
                        >
                          Click here to add questions
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-12 mt-3">
                <button
                  className="btn btn-primary btn-sm"
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? "Saving..."
                    : getValues("id")
                    ? "Update Template"
                    : "Create Template"}
                </button>

                {getValues("id") && (
                  <button
                    className="btn btn-secondary btn-sm ms-2"
                    type="button"
                    onClick={resetForm}
                  >
                    Cancel Edit
                  </button>
                )}

                <button
                  className="btn btn-outline-secondary btn-sm ms-2"
                  type="button"
                  onClick={() => {
                    resetForm();
                    toast.info("Form reset");
                  }}
                >
                  Reset Form
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Right Side - Data Table */}
      <div className="col-md-6">
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="card-title mb-0">Form Templates</h5>
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-success btn-sm"
                onClick={exportToExcel}
                data-bs-toggle="tooltip"
                title="Export to Excel"
              >
                <i className="fas fa-file-excel me-1"></i> Excel
              </button>
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={exportToPDF}
                data-bs-toggle="tooltip"
                title="Export to PDF"
              >
                <i className="fas fa-file-pdf me-1"></i> PDF
              </button>
            </div>
          </div>
          <div className="card-body">
            <div className="table-container fixed-last-column-table">
              <CustomDatatableGrid
                columns={columns}
                data={formTemplatesData}
                onExportExcel={exportToExcel}
                onExportPDF={exportToPDF}
                initialState={{
                  columnPinning: { right: ["actions"] },
                  columnVisibility: {
                    description: false,
                  },
                  pagination: {
                    pageSize: 10,
                    pageIndex: 0,
                  },
                }}
                enableRowSelection={false}
                enableColumnFilters={true}
                enableGlobalFilter={true}
                enablePagination={true}
                enableSorting={true}
                enableColumnResizing={true}
                muiTablePaperProps={{
                  elevation: 0,
                }}
                muiTableContainerProps={{
                  sx: {
                    maxHeight: "500px",
                  },
                }}
                renderTopToolbarCustomActions={() => (
                  <div className="d-flex align-items-center gap-2">
                    <span className="fw-bold">
                      Total: {formTemplatesData.length}
                    </span>
                    <span className="badge bg-success">
                      Active:{" "}
                      {formTemplatesData.filter((ft) => ft.isActive).length}
                    </span>
                    <span className="badge bg-info">
                      Questions:{" "}
                      {formTemplatesData.reduce(
                        (sum, ft) => sum + ft.questionCount,
                        0
                      )}
                    </span>
                  </div>
                )}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FormTemplatesTab;
